import React, { useState, useEffect } from "react";
import TextInput from "../components/TextInput";
import API from "../config/config";
import { useNavigate } from "react-router-dom";

const AddEmployee = () => {
  const navigate = useNavigate();
  const [availableSeniors, setAvailableSeniors] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    fullName: "",
    emailId: "",
    phone: "",
    password: "",
    role: "",
    city: "",
    reportingTo: "",
  });

  const [errors, setErrors] = useState({});

  // Password validation utility
  function validatePassword(password, options = {}) {
    const {
      minLength = 8,
      minLowercase = 1,
      minUppercase = 1,
      minNumbers = 1,
      minSymbols = 1,
    } = options;

    const result = {
      valid: true,
      errors: [],
    };
    if (!password || password.length < minLength) {
      result.valid = false;
      result.errors.push(`Password must be at least ${minLength} characters.`);
    }
    if ((password.match(/[a-z]/g) || []).length < minLowercase) {
      result.valid = false;
      result.errors.push(
        `Password must have at least ${minLowercase} lowercase letter(s).`
      );
    }
    if ((password.match(/[A-Z]/g) || []).length < minUppercase) {
      result.valid = false;
      result.errors.push(
        `Password must have at least ${minUppercase} uppercase letter(s).`
      );
    }
    if ((password.match(/[0-9]/g) || []).length < minNumbers) {
      result.valid = false;
      result.errors.push(
        `Password must have at least ${minNumbers} number(s).`
      );
    }
    if ((password.match(/[^A-Za-z0-9]/g) || []).length < minSymbols) {
      result.valid = false;
      result.errors.push(
        `Password must have at least ${minSymbols} symbol(s).`
      );
    }
    return result;
  }

  // Other field validations
  function validateForm(form) {
    const newErrors = {};
    if (!form.fullName.trim()) newErrors.fullName = "Name is required.";
    if (!form.emailId.trim()) {
      newErrors.emailId = "Email is required.";
    } else if (!/^\S+@\S+\.\S+$/.test(form.emailId)) {
      newErrors.emailId = "Invalid email format.";
    }
    if (!form.phone.trim()) {
      newErrors.phone = "Phone is required.";
    } else if (!/^[0-9]{10}$/.test(form.phone)) {
      newErrors.phone = "Phone must be 10 digits.";
    }
    if (!form.password) {
      newErrors.password = "Password is required.";
    } else {
      const pwResult = validatePassword(form.password, {
        minLength: 8,
        minLowercase: 1,
        minUppercase: 1,
        minNumbers: 1,
        minSymbols: 1,
      });
      if (!pwResult.valid) newErrors.password = pwResult.errors.join(" ");
    }
    if (!form.city) newErrors.city = "City is required.";
    if (!form.role) newErrors.role = "Role is required.";
    if (form.role === "junior" && !form.reportingTo)
      newErrors.reportingTo = "Reporting To is required.";
    return newErrors;
  }

  const roles = {
    admin: "Admin",
    junior: "Junior",
    senior: "Senior",
  };

  const cities = [
    "Hyderabad",
    "Bangalore",
    "Delhi",
    "Mumbai",
    "Chennai",
    "Pune",
    "Ahmedabad",
    "Kolkata",
    "Surat",
    "Jaipur",
    "NA",
  ];

  useEffect(() => {
    const fetchSeniors = async () => {
      if (formData.role === "junior" && formData.city) {
        const token = localStorage.getItem("adminToken");
        if (!token) return;

        try {
          const res = await fetch(
            `${API.ADMIN.GET_SENIORS}?city=${formData.city}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          const data = await res.json();
          if (res.ok) {
            setAvailableSeniors(data.seniors || []);
          } else {
            console.error("Failed to fetch seniors");
            setAvailableSeniors([]);
          }
        } catch (err) {
          console.error("Error fetching seniors:", err);
          setAvailableSeniors([]);
        }
      } else {
        setAvailableSeniors([]);
      }
    };

    fetchSeniors();
  }, [formData.role, formData.city]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if (name === "phone") {
      // Only allow digits and max 10 characters
      newValue = value.replace(/\D/g, "").slice(0, 10);
    }
    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
      ...(name === "role" && newValue !== "junior" && { reportingTo: "" }), // clear reportingTo if not junior
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm(formData);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(API.ADMIN.EMPLOYEES_ADD, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        navigate("/employees");
      } else {
        console.error("Failed to save employee");
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center -mt-12 px-4 bg-gray-100 min-h-screen">
      <form
        className="w-full bg-white shadow-lg rounded-lg p-4"
        onSubmit={handleSubmit}
        style={{
          opacity: loading ? 0.6 : 1,
          pointerEvents: loading ? "none" : "auto",
        }}
      >
        <h2 className="text-2xl font-bold text-center mb-1">Add Employee</h2>
        <hr className="mb-3" />
        {/* Show global errors if any */}
        {Object.keys(errors).length > 0 && (
          <div className="mb-4 text-red-600">Please fix the errors below.</div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <TextInput
              name="fullName"
              label="Name"
              value={formData.fullName}
              onChange={handleChange}
              className="w-full"
              required
            />
            {errors.fullName && (
              <div className="text-red-600 text-xs mt-1">{errors.fullName}</div>
            )}
          </div>
          <div>
            <TextInput
              name="emailId"
              label="Email"
              value={formData.emailId}
              onChange={handleChange}
              className="w-full"
              required
              autoComplete="off"
            />
            {errors.emailId && (
              <div className="text-red-600 text-xs mt-1">{errors.emailId}</div>
            )}
          </div>
          <div>
            <TextInput
              name="phone"
              label="Phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full"
              required
              maxLength={10}
              inputMode="numeric"
              pattern="[0-9]{10}"
            />
            {errors.phone && (
              <div className="text-red-600 text-xs mt-1">{errors.phone}</div>
            )}
          </div>
          <div>
            <TextInput
              name="password"
              label="Password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              className="w-full"
              required
              autoComplete="off"
            />
            {errors.password && (
              <div className="text-red-600 text-xs mt-1">{errors.password}</div>
            )}
          </div>
          <div className="w-full">
            <label
              htmlFor="city"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              City
            </label>
            <select
              name="city"
              value={formData.city}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              required
            >
              <option value="">Select a city</option>
              {cities.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
              {errors.city && (
                <div className="text-red-600 text-xs mt-1">{errors.city}</div>
              )}
            </select>
          </div>

          <div className="w-full">
            <label
              htmlFor="role"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Role
            </label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              required
            >
              <option value="">Select a role</option>
              {Object.entries(roles).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
              {errors.role && (
                <div className="text-red-600 text-xs mt-1">{errors.role}</div>
              )}
            </select>
          </div>

          {formData.role === "junior" && (
            <div className="w-full">
              <label
                htmlFor="reportingTo"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Reporting To
              </label>
              <select
                name="reportingTo"
                value={formData.reportingTo}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                required
              >
                <option value="">Select a senior</option>
                {availableSeniors.map((senior) => (
                  <option key={senior._id} value={senior._id}>
                    {senior.fullName} ({senior.emailId})
                  </option>
                ))}
              </select>
              {errors.reportingTo && (
                <div className="text-red-600 text-xs mt-1">
                  {errors.reportingTo}
                </div>
              )}
            </div>
          )}
        </div>

        <button
          type="submit"
          className="mt-4 w-1/4 bg-yellow-400 text-white font-semibold py-3 rounded-lg hover:bg-yellow-500 transition-colors duration-300 flex items-center justify-center"
          disabled={loading}
        >
          {loading ? (
            <>
              <svg
                className="animate-spin h-5 w-5 mr-2 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                ></path>
              </svg>
              Submitting...
            </>
          ) : (
            "Submit"
          )}
        </button>
      </form>
    </div>
  );
};

export default AddEmployee;
